import { useCallback, useEffect, useState } from 'react';
import { amazonConnectApi, type CommerceSyncHealth, type SellerConnection } from '../../api/amazonconnect';
import { fetchCommerceHealth } from '../commerceHealth';
import { loadDailyBrief } from './loadDailyBrief';
import type { DailyBriefModel } from './types';

export function useDailyBrief(refreshToken = 0) {
  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [model, setModel] = useState<DailyBriefModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void amazonConnectApi
      .listConnections()
      .then((c) => {
        if (cancelled) return;
        setConnections(c);
        setConnectionId((prev) => {
          if (prev && c.some((x) => x.id === prev)) return prev;
          const approved =
            c.find((x) => x.status === 'approved' || x.status === 'connected' || x.status === 'active') ??
            c[0];
          return approved?.id ?? '';
        });
      })
      .catch(() => {
        if (!cancelled) setConnections([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    const conn = connections.find((c) => c.id === connectionId) ?? null;
    let health: CommerceSyncHealth | null = null;
    if (connectionId) {
      health = await fetchCommerceHealth(connectionId);
    }
    try {
      const m = await loadDailyBrief({ connection: conn, health });
      setModel(m);
      if (m.error) setError(m.error);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Daily Brief');
      setModel(null);
    } finally {
      setLoading(false);
    }
  }, [connectionId, connections]);

  useEffect(() => {
    if (!connections.length && !connectionId) {
      // still allow empty brief
      void reload();
      return;
    }
    if (connectionId || connections.length === 0) {
      void reload();
    }
  }, [connectionId, connections, refreshToken, reload]);

  return {
    model,
    loading,
    error,
    connections,
    connectionId,
    setConnectionId,
    reload,
  };
}
